<template>
  <div
    :class="{
      'has-avatar': !isHideAvatar || isKeepAvatarDesign,
      'no-padding': !hasMediaContent,
    }"
    class="content-container"
  >
    <default-overlay />
    <qr-code-component
      :qr-code-data="currentContent?.attrs?.qrCode"
      :has-margin="!!(headline.value || text.value)"
    />
    <media-text
      v-if="headline.value?.trim().length"
      :data="headline"
      :class="{
        headline: true,
        'text-margin': !text.value
      }"
      class="media-text"
      weight="bold"
    />
    <media-text
      v-if="text.value?.trim().length"
      :data="text"
      class="text media-text"
    />
    <div
      :style="{ backgroundColor: currentContent.styles?.background_color }"
      :class="{
        'has-avatar': !isHideAvatar || isKeepAvatarDesign,
      }"
      class="buttons-container"
    >
      <div class="grades">
        <div
          v-for="n in 5"
          :key="n"
          :style="{
            color: n <= grade ? currentContent.styles.active_star_color : currentContent.styles.inactive_star_color,
          }"
          :class="`icon ${icon}`"
          @click="setGrade(n)"
        />
      </div>

      <thank-you-text
        v-if="currentContent.attrs.endText.value && grade !== -1"
      />
    </div>
  </div>
</template>

<script>
import {
  defineComponent,
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue';
import { useStt, wait } from '@ht/lib-plural-vue';
import useSequence from '@/composables/useSequence';
import useContent from '@/composables/useContent';
import useChatbot from '@/composables/useChatbot';
import useAnalytics from '@/composables/useAnalytics';
import useDynamicText from '@/composables/useDynamicText';
import useConfig from '@/composables/useConfig';
import MediaText from '@/components/MediaText';
import DefaultOverlay from '@/components/DefaultOverlay';
import ThankYouText from '@/components/ThankYouText';
import { SYSTEM_VARIABLES_MAP, INTERACTION_DELAY, VARIABLE_VALUE_TYPES } from '@/js/constants';

const STT_RECOGNIZED_MAP = {
  '5 stars': 5,
  '4 stars': 4,
  '3 stars': 3,
  '2 stars': 2,
  '1 star': 1,
};

export default defineComponent({
  name: 'FeedbackComponent',

  components: {
    MediaText,
    ThankYouText,
    DefaultOverlay,
  },

  setup() {
    const { currentContent, isHideAvatar, isKeepAvatarDesign, hasMediaContent } = useContent();
    const { runSequence, interruptSequence, currentSeq } = useSequence();
    const { recognizedText, sttStart, sttStop, sttPhrases } = useStt();
    const { amendAction, lastAnalyticData } = useAnalytics();
    const { getVariable, setVariables, setFlowStateBackToStart } = useConfig();
    const grade = ref(-1);

    const headlineText = useDynamicText(currentContent.value?.attrs?.headline);
    const textText = useDynamicText(currentContent.value?.attrs?.text);

    const headline = computed(() => ({
      ...headlineText.value,
      fontSize: headlineText.value.fontSize ?? 56,
    }));

    const text = computed(() => ({
      ...textText.value,
      fontSize: textText.value.fontSize ?? 36,
    }));

    // workaround for quasar default icon, synchronized with native team
    const icon = computed(() => {
      const star = currentContent.value.styles.star_icon;
      return star === 'r_star' || star === 'star' ? 'icon-mat-star' : star;
    });

    async function setGrade(value) {
      if (grade.value !== -1) {
        return;
      }

      grade.value = value;

      amendAction({
        ratingButtonSelected: grade.value,
      });

      if (currentContent.value.attrs?.variableId) {
        setVariables([{
          id: currentContent.value.attrs.variableId,
          value: { value, type: VARIABLE_VALUE_TYPES.PRIMITIVE },
        }]);
      }

      await wait(currentContent.value.attrs.endText.value ? 1000 : INTERACTION_DELAY);

      const { next } = currentContent.value.attrs;
      interruptSequence(false);
      const nextId = next[value - 1] || next[0];
      setFlowStateBackToStart(nextId);
      runSequence(nextId);
    }

    const { askChatbot } = useChatbot();
    watch(recognizedText, async (value) => {
      const recText = value.text.trim();
      if (recText && grade.value === -1 && value.isFinal) {
        if (Object.prototype.hasOwnProperty.call(STT_RECOGNIZED_MAP, recText)) {
          setGrade(STT_RECOGNIZED_MAP[recText]);
        } else if (currentContent.value.attrs.usePluralDialogs) {
          await askChatbot(recText);
        } else if (currentContent.value.attrs.anythingElse) {
          const next = currentContent.value.attrs.anythingElse;
          interruptSequence();
          setFlowStateBackToStart(next);
          runSequence(next);
        }
      }
    });

    onMounted(() => {
      setVariables([{
        id: SYSTEM_VARIABLES_MAP.CURRENT_DESIGN_ELEMENT.id,
        value: { value: currentSeq.value.id },
      }]);
      sttPhrases([{ variations: Object.keys(STT_RECOGNIZED_MAP) }]);
      sttStart({
        sessionId: getVariable(SYSTEM_VARIABLES_MAP.SESSION_ID.id).value,
        elementId: lastAnalyticData.value.elementId,
      });
    });
    onBeforeUnmount(() => {
      sttStop();
    });

    return {
      currentContent,
      headline,
      text,
      isHideAvatar,
      isKeepAvatarDesign,
      hasMediaContent,
      grade,
      icon,
      setGrade,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/scss/common-ui.scss";

.content-container {
  @extend .content-container;
}

.buttons-container {
  @extend .buttons-container;
}

.grades {
  display: flex;
  justify-content: center;

  @media (orientation: portrait) {
    margin-bottom: clamp(30px, 13vw, 100px);
  }
}

.icon {
  cursor: pointer;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  font-size: clamp(55px, 14vmin, 150px);

  &:not(:first-child) {
    margin-left: clamp(8px, 3vmin, 30px);
  }
}
</style>
